import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import io, { Socket } from 'socket.io-client';
import './GreedyGame.css';
import { GreedyLandingArt } from './GreedyDecor';

const MAX_BAR = 400;
const DEFAULT_LOSS_MARK = 30;
const DEFAULT_MIN_WAGER = 10;
const DEFAULT_GREEDY_MULT = 3;
const DEFAULT_START_UNITS = 200;

type Rgb = { r: number; g: number; b: number };

const COL_RED: Rgb = { r: 236, g: 32, b: 32 };
const COL_BLUE: Rgb = { r: 46, g: 124, b: 224 };
const COL_GREEN: Rgb = { r: 28, g: 214, b: 78 };

function rgb(c: Rgb): string {
  return `rgb(${c.r},${c.g},${c.b})`;
}

function mixRgb(a: Rgb, b: Rgb, t: number): Rgb {
  const k = Math.max(0, Math.min(1, t));
  return {
    r: Math.round(a.r + (b.r - a.r) * k),
    g: Math.round(a.g + (b.g - a.g) * k),
    b: Math.round(a.b + (b.b - a.b) * k),
  };
}

/** Even stack = blue. Ahead → money green. Behind → red. Ends snap at knockout. */
function barFillColor(units: number, lossMark: number, startUnits: number, maxBar: number): string {
  const winMark = Math.max(startUnits + 1, maxBar - lossMark);
  if (units <= lossMark) return rgb(COL_RED);
  if (units >= winMark) return rgb(COL_GREEN);
  if (units <= startUnits) {
    return rgb(mixRgb(COL_RED, COL_BLUE, (units - lossMark) / (startUnits - lossMark)));
  }
  return rgb(mixRgb(COL_BLUE, COL_GREEN, (units - startUnits) / (winMark - startUnits)));
}

export type GreedyRoundLogEntry = {
  round: number;
  w1: number | null;
  w2: number | null;
  type: string;
  transfer: number;
  fromSlot: 1 | 2 | null;
  toSlot: 1 | 2 | null;
  greedyRule: boolean;
  summary: string;
};

export function formatRoundLogDisplay(
  e: GreedyRoundLogEntry
): { line1: string; line2: string } {
  const a = e.w1 == null ? '—' : String(e.w1);
  const b = e.w2 == null ? '—' : String(e.w2);
  const line1 = `Rd ${e.round} · P1 ${a} · P2 ${b}`;

  switch (e.type) {
    case 'double_timeout':
      return { line1, line2: 'Both timed out—no change.' };
    case 'p1_timeout':
      return { line1, line2: `P2 takes ${e.transfer} from P1 (P1 timed out).` };
    case 'p2_timeout':
      return { line1, line2: `P1 takes ${e.transfer} from P2 (P2 timed out).` };
    case 'wagered_tie':
      return { line1, line2: 'Tied wagers—no change.' };
    case 'wagered_win': {
      if (e.greedyRule && e.fromSlot && e.toSlot) {
        return {
          line1,
          line2: `Greedy: P${e.toSlot} takes ${e.transfer} from P${e.fromSlot} (underdog wins).`,
        };
      }
      if (e.fromSlot && e.toSlot && e.transfer > 0) {
        return {
          line1,
          line2: `P${e.toSlot} takes ${e.transfer} from P${e.fromSlot} (higher wager wins).`,
        };
      }
      return { line1, line2: e.summary };
    }
    default:
      return { line1, line2: e.summary };
  }
}

function roundLogItemModifier(e: GreedyRoundLogEntry): string {
  if (e.type === 'wagered_tie' || e.type === 'double_timeout') return 'rlog--neutral';
  if (e.type === 'p1_timeout') return 'rlog--p2win';
  if (e.type === 'p2_timeout') return 'rlog--p1win';
  if (e.type === 'wagered_win' && e.toSlot) {
    if (e.greedyRule) return 'rlog--greedy';
    return e.toSlot === 1 ? 'rlog--p1win' : 'rlog--p2win';
  }
  return 'rlog--neutral';
}

function resultKind(e: GreedyRoundLogEntry): string {
  if (e.type === 'p1_timeout' || e.type === 'p2_timeout') return 'Timeout';
  if (e.type === 'double_timeout') return 'Timeout';
  if (e.type === 'wagered_tie') return 'Tie';
  if (e.type === 'wagered_win' && e.greedyRule) return 'Greedy';
  if (e.type === 'wagered_win') return 'Higher';
  return '';
}

function RoundLogLine2View({
  e,
  name1,
  name2,
}: {
  e: GreedyRoundLogEntry;
  name1: string;
  name2: string;
}): JSX.Element {
  const t = e.transfer;
  const kind = resultKind(e);
  const winnerName = e.toSlot === 1 ? name1 : e.toSlot === 2 ? name2 : null;
  const kindClass =
    kind === 'Greedy' ? 'rlog-pill--greedy' : kind === 'Timeout' ? 'rlog-pill--timeout' : 'rlog-pill--ok';

  if (e.type === 'wagered_tie' || e.type === 'double_timeout') {
    return (
      <>
        <span className="rlog-strong">Draw</span>
        <span className="rlog-muted">
          {' · '}
          {e.type === 'double_timeout' ? 'double timeout' : 'same wager'}
        </span>
      </>
    );
  }

  return (
    <>
      <span className={`rlog-winner rlog-txt--p${e.toSlot}`}>{winnerName}</span>
      <span className="rlog-muted"> Wins </span>
      <span className="rlog-amt">{t}</span>
      {kind ? <span className={`rlog-pill ${kindClass}`}>{kind}</span> : null}
    </>
  );
}

type GreedyRules = {
  minWager: number;
  lossThreshold: number;
  lossThresholdBase?: number;
  greedyMult: number;
  pressure?: boolean;
  pressureAfterRounds?: number;
  pressureStep?: number;
  maxUnitsBar: number;
  startUnits: number;
};

type GreedyState = {
  gameId: string;
  code: string;
  status: 'waiting' | 'playing' | 'ended';
  round: number;
  units1: number;
  units2: number;
  roundEndsAt: number | null;
  wagers: { 1: number | 'submitted' | null; 2: number | 'submitted' | null };
  lockedWagers: boolean;
  youAre: 1 | 2;
  lastRoundMessage: string | null;
  endReason?: string | null;
  result?: string | null;
  p1TimeoutStreak: number;
  p2TimeoutStreak: number;
  consecutiveDoubleTimeouts: number;
  maxUnitsBar: number;
  roundHistory?: GreedyRoundLogEntry[];
  roundSeconds?: number;
  rules?: GreedyRules;
  p1Connected?: boolean;
  p2Connected?: boolean;
  p1Name?: string;
  p2Name?: string;
  reconnectToken?: string;
  rematchRequestedBy?: 1 | 2 | null;
  drawOfferedBy?: 1 | 2 | null;
  vsBot?: boolean;
  botStyle?: 'balanced' | 'trapper' | 'aggressive' | null;
  tableLabel?: string;
};

const GREEDY_RESUME_KEY = 'greedyResume';

type GreedyResume = { gameId: string; token: string };

function readGreedyResume(): GreedyResume | null {
  try {
    const raw = sessionStorage.getItem(GREEDY_RESUME_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw);
    if (p && typeof p.gameId === 'string' && typeof p.token === 'string' && p.token) {
      return { gameId: p.gameId, token: p.token };
    }
  } catch {
    /* ignore */
  }
  return null;
}

function writeGreedyResume(gameId: string, token?: string) {
  if (!gameId || !token) return;
  try {
    sessionStorage.setItem(GREEDY_RESUME_KEY, JSON.stringify({ gameId, token }));
  } catch {
    /* ignore */
  }
}

function clearGreedyResume() {
  try {
    sessionStorage.removeItem(GREEDY_RESUME_KEY);
  } catch {
    /* ignore */
  }
}

const CombinedPoolBar: React.FC<{
  name1: string;
  name2: string;
  units1: number;
  units2: number;
  youAre: 1 | 2;
  lossMark: number;
  startUnits: number;
  maxBar: number;
  p1Connected?: boolean;
  p2Connected?: boolean;
}> = ({ name1, name2, units1, units2, youAre, lossMark, startUnits, maxBar, p1Connected, p2Connected }) => {
  const pool = Math.max(1, units1 + units2);
  const p1Pct = Math.min(100, Math.max(0, (units1 / pool) * 100));
  const leftMarkPct = (lossMark / maxBar) * 100;
  const rightMarkPct = 100 - (lossMark / maxBar) * 100;
  const color1 = barFillColor(units1, lossMark, startUnits, maxBar);
  const color2 = barFillColor(units2, lossMark, startUnits, maxBar);
  const danger1 = units1 < lossMark + 20;
  const danger2 = units2 < lossMark + 20;
  return (
    <div
      className={`greedy-pool${youAre === 1 ? ' greedy-pool--you-p1' : ' greedy-pool--you-p2'}${
        danger1 || danger2 ? ' greedy-pool--danger' : ''
      }`}
    >
      <div className="greedy-pool-heads">
        <div className="greedy-pool-side greedy-pool-side--p1">
          <span className="greedy-pool-who">
            <span className="greedy-pool-slot">P1</span>
            <span className="greedy-pool-name">{name1}</span>
            {youAre === 1 && <span className="greedy-you-chip">You</span>}
            {p1Connected === false && <span className="greedy-dc-chip">Offline</span>}
          </span>
          <span className="greedy-pool-units" style={{ color: color1 }}>
            {units1}
          </span>
        </div>
        <div className="greedy-pool-side greedy-pool-side--p2">
          <span className="greedy-pool-units" style={{ color: color2 }}>
            {units2}
          </span>
          <span className="greedy-pool-who">
            {p2Connected === false && <span className="greedy-dc-chip">Offline</span>}
            {youAre === 2 && <span className="greedy-you-chip">You</span>}
            <span className="greedy-pool-name">{name2}</span>
            <span className="greedy-pool-slot">P2</span>
          </span>
        </div>
      </div>
      <div className="greedy-pool-track" role="img" aria-label={`Player 1 ${units1} Gs, Player 2 ${units2} Gs`}>
        <div className="greedy-pool-danger greedy-pool-danger--left" style={{ width: `${leftMarkPct}%` }} />
        <div className="greedy-pool-danger greedy-pool-danger--right" style={{ width: `${leftMarkPct}%` }} />
        <div
          className="greedy-pool-fill greedy-pool-fill--p1"
          style={{ width: `${p1Pct}%`, background: color1 }}
        />
        <div
          className="greedy-pool-fill greedy-pool-fill--p2"
          style={{ width: `${100 - p1Pct}%`, background: color2 }}
        />
        <div
          className="greedy-pool-split"
          style={{ left: `${p1Pct}%` }}
          title="Divide between the two players"
        />
        <div className="greedy-bar-mark" style={{ left: `${leftMarkPct}%` }} title={`P1 out below ${lossMark}`} />
        <div className="greedy-bar-mark" style={{ left: `${rightMarkPct}%` }} title={`P2 out below ${lossMark}`} />
        <span className="greedy-bar-mark-label greedy-bar-mark-label--left" style={{ left: `${leftMarkPct}%` }}>
          {lossMark}
        </span>
        <span className="greedy-bar-mark-label greedy-bar-mark-label--right" style={{ left: `${rightMarkPct}%` }}>
          {lossMark}
        </span>
      </div>
    </div>
  );
};

const CountdownRing: React.FC<{ secondsLeft: number; total: number; urgent: boolean }> = ({
  secondsLeft,
  total,
  urgent,
}) => {
  const r = 42;
  const c = 2 * Math.PI * r;
  const frac = total > 0 ? Math.max(0, Math.min(1, secondsLeft / total)) : 0;
  return (
    <div className={`greedy-ring${urgent ? ' greedy-ring--urgent' : ''}`}>
      <svg viewBox="0 0 100 100" className="greedy-ring-svg" aria-hidden>
        <circle cx="50" cy="50" r={r} className="greedy-ring-track" />
        <circle
          cx="50"
          cy="50"
          r={r}
          className="greedy-ring-value"
          style={{
            strokeDasharray: `${c}`,
            strokeDashoffset: `${c * (1 - frac)}`,
          }}
        />
      </svg>
      <div className="greedy-ring-num">
        <strong>{secondsLeft}</strong>
        <span>sec</span>
      </div>
    </div>
  );
};

type Auth = {
  isAuthenticated: boolean;
  isGuest: boolean;
  user: { id?: string; username?: string; isAdmin?: boolean } | null;
};

const HOST_ROUND_CHOICES = [10, 30, 60] as const;
const HOST_RATIO_CHOICES = [2, 3, 4] as const;
const BOT_STYLE_CHOICES = [
  { id: 'balanced' as const, label: 'Balanced', blurb: 'Mix of traps and modest overcuts.' },
  { id: 'trapper' as const, label: 'Trapper', blurb: 'Bets low more often and waits for you to get greedy.' },
  { id: 'aggressive' as const, label: 'Aggressive', blurb: 'Pushes higher overcuts. Still tries not to suicide.' },
];

type GreedyLobbyGame = {
  gameId: string;
  hostName: string;
  createdAt: number;
  roundSeconds?: number;
  greedyMult?: number;
  pressure?: boolean;
  tableLabel?: string;
};

function formatTableLabel(greedyMult: number, pressure: boolean) {
  return pressure ? `${greedyMult}-1 · Pressure` : `${greedyMult}-1`;
}

type Props = {
  authState: Auth;
  onBack: () => void;
};

function formatLobbyAge(createdAt: number, now: number) {
  const s = Math.floor((now - createdAt) / 1000);
  if (s < 45) return 'just now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  return `${h}h ago`;
}

function useNarrowWagerUI() {
  const [narrow, setNarrow] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 720px)').matches
  );
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 720px)');
    const on = () => setNarrow(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  return narrow;
}

function clampWager(n: number, maxU: number, minW: number) {
  if (maxU < minW) return minW;
  return Math.min(maxU, Math.max(minW, Math.floor(n)));
}

const FIRST_NUDGE_REPEAT_MS = 360;

function repeatDelayForNudgeTick(accumulatedAfterStep: number) {
  if (accumulatedAfterStep < 10) return 300;
  if (accumulatedAfterStep < 30) return 130;
  return 60;
}

function repeatNudgeStep(value: number, autoTick: number) {
  if (autoTick < 30) return 1;
  if (value < 50) return 2;
  if (value < 100) return 5;
  return 10;
}

function useNudgeRepeatHold(
  apply: (f: (cur: number) => number) => void,
  maxUnits: number,
  minWager: number
) {
  const maxRef = useRef(maxUnits);
  maxRef.current = maxUnits;
  const minRef = useRef(minWager);
  minRef.current = minWager;
  const holdTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const winListenersRef = useRef<(() => void) | null>(null);
  const holdActiveRef = useRef(false);

  const endNudgeHold = useCallback(() => {
    if (holdTimerRef.current) {
      clearTimeout(holdTimerRef.current);
      holdTimerRef.current = null;
    }
    if (winListenersRef.current) {
      winListenersRef.current();
      winListenersRef.current = null;
    }
    holdActiveRef.current = false;
  }, []);

  const startNudgeHold = useCallback(
    (dir: 1 | -1) => {
      endNudgeHold();
      holdActiveRef.current = true;
      apply((cur) => {
        const minv = minRef.current;
        const c = clampWager(cur, maxRef.current, minv);
        return dir === 1 ? Math.min(maxRef.current, c + 1) : Math.max(minv, c - 1);
      });
      const onWindowUp = () => endNudgeHold();
      window.addEventListener('pointerup', onWindowUp);
      window.addEventListener('pointercancel', onWindowUp);
      winListenersRef.current = () => {
        window.removeEventListener('pointerup', onWindowUp);
        window.removeEventListener('pointercancel', onWindowUp);
      };
      let autoTick = 0;
      const runTick = () => {
        if (!holdActiveRef.current) return;
        const maxu = maxRef.current;
        const minv = minRef.current;
        apply((cur) => {
          const c = clampWager(cur, maxu, minv);
          const step = repeatNudgeStep(c, autoTick);
          if (dir === 1) return Math.min(maxu, c + step);
          return Math.max(minv, c - step);
        });
        autoTick += 1;
        if (!holdActiveRef.current) return;
        holdTimerRef.current = setTimeout(runTick, repeatDelayForNudgeTick(autoTick - 1));
      };
      holdTimerRef.current = setTimeout(runTick, FIRST_NUDGE_REPEAT_MS);
    },
    [apply, endNudgeHold]
  );

  useEffect(
    () => () => {
      endNudgeHold();
    },
    [endNudgeHold]
  );

  return { startNudgeHold, endNudgeHold };
}

type WagerPanelProps = {
  value: number;
  maxUnits: number;
  minWager: number;
  onChange: (n: number) => void;
  onLock: () => void;
  opponentHasLocked: boolean;
  opponentLockedLabel?: string;
  narrow: boolean;
};

const WagerPanel: React.FC<WagerPanelProps> = ({
  value,
  maxUnits,
  minWager,
  onChange,
  onLock,
  opponentHasLocked,
  opponentLockedLabel = 'Opponent locked in',
  narrow,
}) => {
  const v = clampWager(value, maxUnits, minWager);
  const vRef = useRef(v);
  vRef.current = v;
  const applyFromNumber = useCallback(
    (f: (cur: number) => number) => {
      onChange(clampWager(f(vRef.current), maxUnits, minWager));
    },
    [onChange, maxUnits, minWager]
  );
  const { startNudgeHold } = useNudgeRepeatHold(applyFromNumber, maxUnits, minWager);

  return (
    <div className="greedy-wager-slider-ui">
      <p className={`greedy-wager-opp${opponentHasLocked ? ' is-on' : ''}`} role="status">
        {opponentHasLocked ? opponentLockedLabel : ''}
      </p>
      <div className="greedy-wager-readout">
        <span className="greedy-wager-label">Your wager</span>
        <span className="greedy-wager-box" aria-live="polite">
          {v}
        </span>
        <span className="greedy-wager-bounds">
          {minWager}–{maxUnits}
        </span>
      </div>
      <div className="greedy-wager-slider-row2">
        <button
          type="button"
          className="greedy-wager-nudge-btn"
          aria-label="Decrease wager"
          disabled={v <= minWager}
          onPointerDown={(e) => {
            e.preventDefault();
            startNudgeHold(-1);
          }}
        >
          −
        </button>
        <input
          type="range"
          className="greedy-wager-range-input"
          min={minWager}
          max={maxUnits}
          step={1}
          value={v}
          disabled={maxUnits < minWager}
          aria-label="Wager amount"
          onChange={(e) => onChange(Number(e.target.value))}
        />
        <button
          type="button"
          className="greedy-wager-nudge-btn"
          aria-label="Increase wager"
          disabled={v >= maxUnits}
          onPointerDown={(e) => {
            e.preventDefault();
            startNudgeHold(1);
          }}
        >
          +
        </button>
      </div>
      {!narrow && (
        <div className="greedy-wager-typed">
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            autoComplete="off"
            className="greedy-input greedy-wager-dt-input"
            value={String(v)}
            onChange={(e) => {
              const raw = e.target.value.replace(/[^\d]/g, '');
              if (raw === '') {
                onChange(minWager);
                return;
              }
              onChange(clampWager(parseInt(raw, 10), maxUnits, minWager));
            }}
          />
        </div>
      )}
      <button type="button" className="greedy-btn primary greedy-wager-lock" onClick={onLock}>
        Lock wager
      </button>
    </div>
  );
};

export const GreedyGame: React.FC<Props> = ({ authState, onBack }) => {
  const [socket, setSocket] = useState<Socket | null>(null);
  const [connected, setConnected] = useState(false);
  const [state, setState] = useState<GreedyState | null>(null);
  const [lobbyGames, setLobbyGames] = useState<GreedyLobbyGame[]>([]);
  const [wagerDraft, setWagerDraft] = useState(DEFAULT_MIN_WAGER);
  const [error, setError] = useState<string | null>(null);
  const [nowTick, setNowTick] = useState(Date.now());
  const [hostRoundSeconds, setHostRoundSeconds] = useState<(typeof HOST_ROUND_CHOICES)[number]>(60);
  const [hostGreedyMult, setHostGreedyMult] = useState<(typeof HOST_RATIO_CHOICES)[number]>(3);
  const [hostPressure, setHostPressure] = useState(false);
  const [dealtNote, setDealtNote] = useState<string | null>(null);
  const [playVs, setPlayVs] = useState<'person' | 'bot'>('bot');
  const [botStyle, setBotStyle] = useState<(typeof BOT_STYLE_CHOICES)[number]['id']>('balanced');
  const [showRules, setShowRules] = useState(false);
  const narrowWager = useNarrowWagerUI();

  const canHost = authState.isAuthenticated && !authState.isGuest;
  const selectedBot = BOT_STYLE_CHOICES.find((s) => s.id === botStyle) || BOT_STYLE_CHOICES[0];
  const minWager = state?.rules?.minWager ?? DEFAULT_MIN_WAGER;
  const lossMark = state?.rules?.lossThreshold ?? DEFAULT_LOSS_MARK;
  const greedyMult = state?.rules?.greedyMult ?? DEFAULT_GREEDY_MULT;
  const pressureOn = Boolean(state?.rules?.pressure);
  const startUnits = state?.rules?.startUnits ?? DEFAULT_START_UNITS;
  const tableLabel =
    state?.tableLabel || formatTableLabel(greedyMult, pressureOn);
  const maxBar = state?.maxUnitsBar ?? state?.rules?.maxUnitsBar ?? MAX_BAR;

  const socketUrl = useMemo(() => {
    if (process.env.NODE_ENV !== 'production') {
      return 'http://localhost:3002';
    }
    return (
      (process.env.REACT_APP_SOCKET_URL || '').trim() ||
      'https://api.migoyugo.com'
    );
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    const s = io(socketUrl, {
      auth: {
        token,
        isGuest: authState.isGuest,
        user: authState.user,
      },
      forceNew: true,
      reconnection: true,
    });
    setSocket(s);
    const applyState = (st: GreedyState) => {
      setState(st);
      if (st.status === 'ended') clearGreedyResume();
      else writeGreedyResume(st.gameId, st.reconnectToken);
    };

    s.on('connect', () => {
      setConnected(true);
      setError(null);
      const resume = readGreedyResume();
      if (resume) s.emit('greedy:rejoin', resume);
      s.emit('greedy:requestLobby');
    });
    s.on('disconnect', () => setConnected(false));
    s.on('connect_error', () => {
      setConnected(false);
      setError('Cannot reach the local game server (port 3002).');
    });
    s.on('greedy:lobby', (p: { games?: GreedyLobbyGame[] }) => {
      setLobbyGames(Array.isArray(p?.games) ? p.games : []);
    });
    s.on('greedy:created', (p: { state: GreedyState }) => {
      setError(null);
      applyState(p.state);
    });
    s.on('greedy:reconnected', (p: { state: GreedyState }) => {
      setError(null);
      applyState(p.state);
    });
    s.on('greedy:state', (p: { state: GreedyState }) => {
      applyState(p.state);
    });
    s.on('greedy:gameOver', (p: { state: GreedyState }) => {
      applyState(p.state);
    });
    s.on('greedy:error', (p: { message?: string }) => {
      setError(p.message || 'Error');
    });
    return () => {
      s.disconnect();
      setSocket(null);
    };
  }, [socketUrl, authState.isGuest]);

  useEffect(() => {
    const t = window.setInterval(() => setNowTick(Date.now()), 200);
    return () => window.clearInterval(t);
  }, []);

  const backToLobby = useCallback(() => {
    clearGreedyResume();
    setState(null);
    setError(null);
    setWagerDraft(minWager);
    socket?.emit('greedy:requestLobby');
  }, [socket, minWager]);

  const requestRematch = useCallback(() => {
    if (!socket || !state?.gameId) return;
    setError(null);
    socket.emit('greedy:requestRematch', { gameId: state.gameId });
  }, [socket, state?.gameId]);

  const respondRematch = useCallback(
    (accept: boolean) => {
      if (!socket || !state?.gameId) return;
      setError(null);
      socket.emit('greedy:respondRematch', { gameId: state.gameId, accept });
    },
    [socket, state?.gameId]
  );

  const resignGame = useCallback(() => {
    if (!socket || !state?.gameId) return;
    setError(null);
    socket.emit('greedy:resign', { gameId: state.gameId });
  }, [socket, state?.gameId]);

  const offerDraw = useCallback(() => {
    if (!socket || !state?.gameId) return;
    setError(null);
    socket.emit('greedy:offerDraw', { gameId: state.gameId });
  }, [socket, state?.gameId]);

  const respondDraw = useCallback(
    (accept: boolean) => {
      if (!socket || !state?.gameId) return;
      setError(null);
      socket.emit('greedy:respondDraw', { gameId: state.gameId, accept });
    },
    [socket, state?.gameId]
  );

  const exitBotGame = useCallback(() => {
    if (state?.gameId) socket?.emit('greedy:leave', { gameId: state.gameId });
    backToLobby();
  }, [socket, state?.gameId, backToLobby]);

  const dealTable = useCallback(() => {
    let nextMult: (typeof HOST_RATIO_CHOICES)[number] = hostGreedyMult;
    let nextPressure = hostPressure;
    let guard = 0;
    do {
      nextMult = HOST_RATIO_CHOICES[Math.floor(Math.random() * HOST_RATIO_CHOICES.length)];
      nextPressure = Math.random() < 0.5;
      guard += 1;
    } while (nextMult === hostGreedyMult && nextPressure === hostPressure && guard < 8);
    setHostGreedyMult(nextMult);
    setHostPressure(nextPressure);
    setDealtNote(formatTableLabel(nextMult, nextPressure));
  }, [hostGreedyMult, hostPressure]);

  const createRoom = useCallback(() => {
    if (!socket) return;
    if (!canHost) {
      setError('Sign in to start a game as host (Player #1).');
      return;
    }
    setError(null);
    socket.emit('greedy:create', {
      roundSeconds: hostRoundSeconds,
      greedyMult: hostGreedyMult,
      pressure: hostPressure,
    });
  }, [socket, canHost, hostRoundSeconds, hostGreedyMult, hostPressure]);

  const createBotGame = useCallback(() => {
    if (!socket) return;
    if (!connected) {
      setError('Connecting to the server…');
      return;
    }
    setError(null);
    if (state?.gameId) socket.emit('greedy:leave', { gameId: state.gameId });
    socket.emit('greedy:create', {
      roundSeconds: hostRoundSeconds,
      vsBot: true,
      botStyle,
      greedyMult: hostGreedyMult,
      pressure: hostPressure,
    });
  }, [socket, connected, hostRoundSeconds, botStyle, state?.gameId, hostGreedyMult, hostPressure]);

  const joinByGameId = useCallback(
    (gameId: string) => {
      if (!socket || !gameId) return;
      setError(null);
      socket.emit('greedy:join', { gameId });
    },
    [socket]
  );

  const secondsLeft =
    state?.roundEndsAt && state.status === 'playing' && !state.lockedWagers
      ? Math.max(0, Math.ceil((state.roundEndsAt - nowTick) / 1000))
      : null;

  const myUnits = state ? (state.youAre === 1 ? state.units1 : state.units2) : 0;
  const myWager = state?.wagers[state.youAre];
  const canSubmit =
    state?.status === 'playing' &&
    !state.lockedWagers &&
    typeof myWager !== 'number' &&
    secondsLeft !== null &&
    secondsLeft > 0;
  const opponentWager = state ? state.wagers[state.youAre === 1 ? 2 : 1] : undefined;
  const opponentHasLocked = Boolean(canSubmit && opponentWager === 'submitted');

  const submitWager = useCallback(() => {
    if (!socket || !state?.gameId) return;
    const n = clampWager(wagerDraft, myUnits, minWager);
    if (n < minWager || n > myUnits) {
      setError(`Wager must be between ${minWager} and ${myUnits} Gs.`);
      return;
    }
    setError(null);
    socket.emit('greedy:submitWager', { gameId: state.gameId, amount: n });
  }, [socket, state?.gameId, wagerDraft, myUnits, minWager]);

  const wagerKey = state ? `${state.gameId}-r${state.round}` : '';

  useEffect(() => {
    if (!canSubmit) return;
    setWagerDraft(minWager);
  }, [wagerKey, canSubmit, minWager]);

  useEffect(() => {
    if (!canSubmit) return;
    setWagerDraft((d) => Math.min(Math.max(d, minWager), myUnits));
  }, [myUnits, canSubmit, minWager]);

  const roundTotal = state?.roundSeconds ?? 60;

  return (
    <div className="greedy-page">
      <div className="greedy-header">
        <button type="button" className="greedy-back" onClick={onBack}>
          ← Migoyugo
        </button>
        <div className="greedy-brand">
          <h1 className="greedy-title">Greedy</h1>
        </div>
        <div className="greedy-header-right">
          <button type="button" className="greedy-rules-toggle" onClick={() => setShowRules((s) => !s)}>
            Rules
          </button>
          <div className="greedy-conn">
            <span className={`greedy-dot${connected ? ' on' : ''}`} />
            {connected ? 'Live' : 'Offline'}
          </div>
        </div>
      </div>

      {showRules && (
        <div className="greedy-rules-card">
          <div className="greedy-rules-head">
            <h2>Greedy Game Rules</h2>
            <button
              type="button"
              className="greedy-rules-x"
              aria-label="Close rules"
              onClick={() => setShowRules(false)}
            >
              ×
            </button>
          </div>

          <h3>The Game</h3>
          <ul>
            <li>
              <strong>Greedy</strong> is a two-player wagering game. Each player starts with 200 Gold Units
              (Gs)
            </li>
            <li>There are 400 Gs in total; they only move from one player to the other</li>
            <li>Play rounds until someone loses</li>
          </ul>

          <h3>Each Round</h3>
          <ul>
            <li>Both players secretly wager Gs</li>
            <li>
              A wager must be at least {minWager} Gs, and no more than you currently hold
            </li>
            <li>Both players have the same time to lock (60 seconds by default)</li>
            <li>Once locked, a wager cannot be changed</li>
            <li>Wagers stay hidden until both are locked, or until the round ends</li>
          </ul>

          <h3>Who Wins the Round</h3>
          <ul>
            <li>Equal wagers: draw, no Gs move</li>
            <li>Different wagers: the higher wager wins, and takes the other player’s wager</li>
            <li>Exception: the Greedy rule, below</li>
          </ul>

          <h3>The Greedy Rule</h3>
          <ul>
            <li>
              If the higher wager is more than {greedyMult}× the lower wager, the lower wager wins
            </li>
            <li>That winner still takes the other player’s wager</li>
            <li>Exactly {greedyMult}× is not Greedy</li>
            <li>
              {greedyMult === 2 && 'Example: 20 vs 10 — 20 takes 10. 21 vs 10 — 10 takes 21'}
              {greedyMult === 4 && 'Example: 40 vs 10 — 40 takes 10. 41 vs 10 — 10 takes 41'}
              {greedyMult !== 2 && greedyMult !== 4 && 'Example: 25 vs 20 — 25 takes 20. 40 vs 10 — 10 takes 40'}
            </li>
          </ul>

          <h3>Starting options</h3>
          <ul>
            <li>Ratio is 2-1, 3-1, or 4-1. A player is out below 20, 30, or 40 Gs</li>
            <li>
              Pressure (optional): after round 10, the out-line rises 5 Gs per round. A player just
              under the new line still plays that round
            </li>
            <li>
              Randomize Ratio and Pressure to pick both at random. Both players start with 200 Gs
              either way
            </li>
          </ul>

          <h3>Winning the Game</h3>
          <ul>
            <li>
              After any round, if a player has fewer than {lossMark} Gs, that player loses
              {pressureOn ? ` (Pressure is on; the out-line can rise)` : ''}
            </li>
          </ul>

          <h3>Timeouts</h3>
          <ul>
            <li>If you do not lock before time runs out, you time out</li>
            <li>
              If you disconnect, the clock keeps running. Rejoin and lock in time, or it is a timeout
            </li>
            <li>
              Only you time out: you lose the round and pay their wager, but never more than you still hold
            </li>
            <li>Both time out: draw, no Gs move</li>
            <li>Time out 3 rounds in a row: you lose the game</li>
            <li>
              Both time out 3 rounds in a row: the player with more Gs wins; if tied, the game is drawn
            </li>
            <li>Locking a wager breaks your timeout streak</li>
          </ul>
          <div className="greedy-rules-foot">
            <button
              type="button"
              className="greedy-rules-x"
              aria-label="Close rules"
              onClick={() => setShowRules(false)}
            >
              ×
            </button>
          </div>
        </div>
      )}

      {error && <div className="greedy-error">{error}</div>}

      {!state && (
        <div className="greedy-landing">
          <div className="greedy-hero">
            <GreedyLandingArt />
            <h2 className="greedy-landing-title">A Two Player Wagering Game</h2>
            <p className="greedy-landing-tagline">The Greedy Get Needy</p>
          </div>
          <div className="greedy-lobby-grid">
            <section className="greedy-panel greedy-panel--start" aria-labelledby="greedy-start-heading">
              <h3 id="greedy-start-heading" className="greedy-panel-title">
                Start a game
              </h3>
              <p className="greedy-round-timer-label" id="greedy-vs-label">
                Play against
              </p>
              <div className="greedy-round-choices" role="group" aria-labelledby="greedy-vs-label">
                <button
                  type="button"
                  className={playVs === 'bot' ? 'greedy-seg active' : 'greedy-seg'}
                  onClick={() => setPlayVs('bot')}
                >
                  A bot
                </button>
                <button
                  type="button"
                  className={playVs === 'person' ? 'greedy-seg active' : 'greedy-seg'}
                  onClick={() => setPlayVs('person')}
                >
                  A person
                </button>
              </div>

              <p className="greedy-round-timer-label" id="greedy-ratio-label">
                Greedy ratio
              </p>
              <div className="greedy-round-choices" role="group" aria-labelledby="greedy-ratio-label">
                {HOST_RATIO_CHOICES.map((k) => (
                  <button
                    key={k}
                    type="button"
                    className={hostGreedyMult === k ? 'greedy-seg active' : 'greedy-seg'}
                    onClick={() => {
                      setHostGreedyMult(k);
                      setDealtNote(null);
                    }}
                  >
                    {k}-1
                  </button>
                ))}
              </div>
              <p className="greedy-option-hint">Out-line at {hostGreedyMult * 10}</p>

              <p className="greedy-round-timer-label" id="greedy-pressure-label">
                Pressure
              </p>
              <div className="greedy-round-choices" role="group" aria-labelledby="greedy-pressure-label">
                <button
                  type="button"
                  className={!hostPressure ? 'greedy-seg active' : 'greedy-seg'}
                  onClick={() => {
                    setHostPressure(false);
                    setDealtNote(null);
                  }}
                >
                  Off
                </button>
                <button
                  type="button"
                  className={hostPressure ? 'greedy-seg active' : 'greedy-seg'}
                  onClick={() => {
                    setHostPressure(true);
                    setDealtNote(null);
                  }}
                >
                  On
                </button>
              </div>
              <p className="greedy-option-hint">
                {hostPressure
                  ? 'After round 10, the out-line rises 5 Gs per round'
                  : `Out-line stays at ${hostGreedyMult * 10}`}
              </p>

              <div className="greedy-deal-row">
                <button type="button" className="greedy-btn greedy-deal-btn" onClick={dealTable}>
                  Randomize Ratio and Pressure
                </button>
                {dealtNote ? <p className="greedy-deal-note">Randomized {dealtNote}</p> : null}
              </div>

              <p className="greedy-round-timer-label" id="greedy-round-timer-label">
                Seconds to lock a wager
              </p>
              <div className="greedy-round-choices" role="group" aria-labelledby="greedy-round-timer-label">
                {HOST_ROUND_CHOICES.map((sec) => (
                  <button
                    key={sec}
                    type="button"
                    className={hostRoundSeconds === sec ? 'greedy-seg active' : 'greedy-seg'}
                    onClick={() => setHostRoundSeconds(sec)}
                  >
                    {sec}s
                  </button>
                ))}
              </div>

              {playVs === 'bot' ? (
                <>
                  <p className="greedy-round-timer-label" id="greedy-bot-style-label">
                    How the bot plays
                  </p>
                  <div className="greedy-round-choices greedy-round-choices--wrap" role="group" aria-labelledby="greedy-bot-style-label">
                    {BOT_STYLE_CHOICES.map((s) => (
                      <button
                        key={s.id}
                        type="button"
                        className={botStyle === s.id ? 'greedy-seg active' : 'greedy-seg'}
                        onClick={() => setBotStyle(s.id)}
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                  <p className="greedy-panel-desc">{selectedBot.blurb}</p>
                  <button
                    type="button"
                    className="greedy-btn greedy-btn-block primary"
                    onClick={createBotGame}
                    disabled={!connected}
                  >
                    {connected ? 'Play vs Bot' : 'Connecting to server…'}
                  </button>
                </>
              ) : canHost ? (
                <>
                  <p className="greedy-panel-desc">
                    You become Player #1. The room appears in the open list so Player #2 can join.
                  </p>
                  <button
                    type="button"
                    className="greedy-btn greedy-btn-block primary"
                    onClick={createRoom}
                    disabled={!connected}
                  >
                    Start a game
                  </button>
                </>
              ) : (
                <p className="greedy-panel-note">Sign in (not as guest) to host a game against a person.</p>
              )}
            </section>
            <section className="greedy-panel greedy-panel--join" aria-labelledby="greedy-join-heading">
              <h3 id="greedy-join-heading" className="greedy-panel-title">
                Join a game
              </h3>
              <p className="greedy-panel-desc">Open rooms waiting for Player #2. Use another browser to test locally.</p>
              {!connected && <p className="greedy-panel-note">Connecting to the server…</p>}
              {connected && lobbyGames.length === 0 && (
                <p className="greedy-lobby-empty">No open games. Start one, or wait for a host.</p>
              )}
              {lobbyGames.length > 0 && (
                <ul className="greedy-lobby-list" aria-label="Open Greedy games">
                  {lobbyGames.map((g) => (
                    <li key={g.gameId} className="greedy-lobby-row">
                      <div className="greedy-lobby-row-main">
                        <span className="greedy-lobby-host">{g.hostName}</span>
                        <span className="greedy-lobby-age">
                          {`${g.tableLabel || formatTableLabel(g.greedyMult || 3, Boolean(g.pressure))} · `}
                          {g.roundSeconds != null ? `${g.roundSeconds}s · ` : ''}
                          {formatLobbyAge(g.createdAt, nowTick)}
                        </span>
                      </div>
                      <button
                        type="button"
                        className="greedy-btn greedy-btn-join primary"
                        onClick={() => joinByGameId(g.gameId)}
                        disabled={!connected}
                      >
                        Join
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>
        </div>
      )}

      {state && state.status === 'waiting' && (
        <div className="greedy-wait">
          <div className="greedy-wait-pulse" aria-hidden />
          <h2 className="greedy-wait-title">Waiting for Player #2</h2>
          <p className="greedy-wait-text">
            Your room is in the join list. Open this site in another browser or incognito and tap Join.
          </p>
          <p className="greedy-muted">
            {tableLabel}
            {state.roundSeconds != null ? ` · ${state.roundSeconds}s per round` : ''}. Waiting for
            someone to sit down…
          </p>
          <button
            type="button"
            className="greedy-btn"
            onClick={() => {
              if (state.gameId) socket?.emit('greedy:leave', { gameId: state.gameId });
              backToLobby();
            }}
          >
            Cancel
          </button>
        </div>
      )}

      {state && (state.status === 'playing' || state.status === 'ended') && (
        <div className="greedy-match">
          {secondsLeft !== null && state.status === 'playing' && !state.lockedWagers && (
            <div className={`greedy-timer-block${secondsLeft <= 8 ? ' is-urgent' : ''}`}>
              <CountdownRing
                secondsLeft={secondsLeft}
                total={roundTotal}
                urgent={secondsLeft <= 8}
              />
              <div className="greedy-timer-copy">
                <span className="greedy-timer-label">Round {state.round}</span>
                <span className="greedy-table-chip">
                  {tableLabel}
                  {pressureOn ? ` · out below ${lossMark}` : ''}
                </span>
              </div>
            </div>
          )}

          <div className="greedy-bars">
            <CombinedPoolBar
              name1={state.p1Name || 'Player 1'}
              name2={state.p2Name || 'Player 2'}
              units1={state.units1}
              units2={state.units2}
              youAre={state.youAre}
              lossMark={lossMark}
              startUnits={startUnits}
              maxBar={maxBar}
              p1Connected={state.p1Connected}
              p2Connected={state.p2Connected}
            />
          </div>

          {state.lastRoundMessage && <p className="greedy-msg">{state.lastRoundMessage}</p>}

          <div className="greedy-meta">
            <span>
              {tableLabel} · out below {lossMark}
            </span>
            <span>Double timeout {state.consecutiveDoubleTimeouts}/3</span>
            <span>
              Timeouts P1 {state.p1TimeoutStreak}/3 · P2 {state.p2TimeoutStreak}/3
            </span>
          </div>

          {state.status === 'playing' && (
            <div className="greedy-match-actions">
              {state.vsBot ? (
                <>
                  <button type="button" className="greedy-btn" onClick={resignGame}>
                    Resign
                  </button>
                  <button type="button" className="greedy-btn" onClick={exitBotGame}>
                    Exit
                  </button>
                </>
              ) : (
                <>
                  {state.drawOfferedBy === state.youAre && (
                    <p className="greedy-draw-note">Draw offered. Waiting for opponent…</p>
                  )}
                  {state.drawOfferedBy && state.drawOfferedBy !== state.youAre && (
                    <>
                      <p className="greedy-draw-note">
                        {(state.drawOfferedBy === 1 ? state.p1Name : state.p2Name) || 'Opponent'} offers a
                        draw.
                      </p>
                      <button type="button" className="greedy-btn primary" onClick={() => respondDraw(true)}>
                        Accept draw
                      </button>
                      <button type="button" className="greedy-btn" onClick={() => respondDraw(false)}>
                        Decline
                      </button>
                    </>
                  )}
                  {!state.drawOfferedBy && (
                    <button type="button" className="greedy-btn" onClick={offerDraw}>
                      Offer draw
                    </button>
                  )}
                  <button type="button" className="greedy-btn" onClick={resignGame}>
                    Resign
                  </button>
                </>
              )}
            </div>
          )}

          {state.status === 'playing' && !state.lockedWagers && (
            <div className="greedy-wager-dock">
              <div className="greedy-wager-panel">
                {typeof myWager === 'number' ? (
                  <div className="greedy-locked-card">
                    <p className="greedy-locked-kicker">Locked in</p>
                    <p className="greedy-locked-amt">{myWager}</p>
                    <p className="greedy-locked-wait">
                      {opponentWager === 'submitted'
                        ? 'Resolving…'
                        : state.vsBot
                          ? 'Waiting for Bot. Your wager is hidden.'
                          : 'Waiting for opponent. Your wager is hidden.'}
                    </p>
                  </div>
                ) : (
                  canSubmit && (
                    <WagerPanel
                      value={wagerDraft}
                      maxUnits={myUnits}
                      minWager={minWager}
                      onChange={setWagerDraft}
                      onLock={submitWager}
                      opponentHasLocked={opponentHasLocked}
                      opponentLockedLabel={state.vsBot ? 'Bot locked in' : 'Opponent locked in'}
                      narrow={narrowWager}
                    />
                  )
                )}
              </div>
            </div>
          )}

          {Array.isArray(state.roundHistory) && state.roundHistory.length > 0 && (
            <section className="greedy-round-log" aria-label="Round history, newest at top">
              <h2 className="greedy-round-log-title">Round history</h2>
              <ul className="greedy-round-log-list">
                {state.roundHistory.map((entry, i) => {
                  const a = entry.w1 == null ? '—' : String(entry.w1);
                  const b = entry.w2 == null ? '—' : String(entry.w2);
                  return (
                    <li
                      key={`${entry.round}-${i}-${entry.type}-${entry.transfer}`}
                      className={`greedy-round-log-item ${roundLogItemModifier(entry)}`}
                    >
                      <div className="greedy-round-log-l1">
                        <span className="rlog-rd">R{entry.round}</span>
                        <span className="rlog-sep">·</span>
                        <span className="rlog-p1b">
                          <span className="rlog-tag rlog-tag--p1">P1</span>
                          <span className="rlog-amt rlog-amt--p1">{a}</span>
                        </span>
                        <span className="rlog-sep">·</span>
                        <span className="rlog-p2b">
                          <span className="rlog-tag rlog-tag--p2">P2</span>
                          <span className="rlog-amt rlog-amt--p2">{b}</span>
                        </span>
                      </div>
                      <div className="greedy-round-log-l2">
                        <RoundLogLine2View
                          e={entry}
                          name1={state.p1Name || 'Player 1'}
                          name2={state.p2Name || 'Player 2'}
                        />
                      </div>
                    </li>
                  );
                })}
              </ul>
            </section>
          )}

          {state.status === 'ended' &&
            (() => {
              const r = state.result;
              const isDraw = !r || r === 'draw';
              const youWon = (r === 'p1' && state.youAre === 1) || (r === 'p2' && state.youAre === 2);
              const tone = isDraw ? 'draw' : youWon ? 'win' : 'lose';
              const name1 = state.p1Name || 'Player 1';
              const name2 = state.p2Name || 'Player 2';
              const rematchBy = state.rematchRequestedBy;
              const rematchMode =
                rematchBy === state.youAre ? 'waiting' : rematchBy ? 'incoming' : 'idle';
              const requesterName = rematchBy === 1 ? name1 : rematchBy === 2 ? name2 : '';
              const oppConnected = state.youAre === 1 ? state.p2Connected !== false : state.p1Connected !== false;
              return (
                <div className={`greedy-over greedy-over--${tone}`}>
                  <h2 className="greedy-over-title">
                    {isDraw && 'Draw'}
                    {!isDraw && youWon && 'You won'}
                    {!isDraw && !youWon && `${r === 'p1' ? name1 : name2} won`}
                  </h2>
                  {rematchMode === 'waiting' && (
                    <p className="greedy-rematch-note">Rematch sent. Waiting for opponent…</p>
                  )}
                  {rematchMode === 'incoming' && (
                    <p className="greedy-rematch-note">{requesterName} wants a rematch.</p>
                  )}
                  <div className="greedy-over-actions">
                    {rematchMode === 'idle' && (
                      <button
                        type="button"
                        className="greedy-btn primary"
                        onClick={requestRematch}
                        disabled={!oppConnected}
                      >
                        {state.vsBot ? 'Play again' : 'Request rematch'}
                      </button>
                    )}
                    {rematchMode === 'incoming' && (
                      <>
                        <button type="button" className="greedy-btn primary" onClick={() => respondRematch(true)}>
                          Accept rematch
                        </button>
                        <button type="button" className="greedy-btn" onClick={() => respondRematch(false)}>
                          Decline
                        </button>
                      </>
                    )}
                    <button type="button" className="greedy-btn greedy-over-again" onClick={backToLobby}>
                      Back to lobby
                    </button>
                  </div>
                </div>
              );
            })()}
        </div>
      )}
    </div>
  );
};

export default GreedyGame;
